import React, { useState } from 'react';
import { Icon } from '@iconify/react';
import { usePageTitle } from '../../hooks/usePageTitle';
import { adminApi } from '../../api/client';
import { useQuery } from '@tanstack/react-query';
import { useContentCreators, useGrantUploadAccess, useRevokeUploadAccess } from '../../hooks/useMovie';
import toast from 'react-hot-toast';
import { getApiErrorMessage } from '../../api/errors';
import { Button } from '../../components/ui/button';

function useCreatorContent(userId, enabled) {
  return useQuery({
    queryKey: ['admin', 'content-creators', userId, 'content'],
    queryFn: async () => {
      const { data } = await adminApi.getContentCreatorContent(userId);
      if (!data.success) throw new Error(data.message || 'Failed to load creator content');
      return data.data;
    },
    enabled,
  });
}

const STATUS_STYLES = {
  Approved: 'bg-emerald-500/10 text-emerald-400 ring-emerald-500/25',
  Pending: 'bg-primary/10 text-primary ring-primary/25',
  Processing: 'bg-cyan-500/10 text-cyan-400 ring-cyan-500/25',
  Rejected: 'bg-destructive/10 text-destructive ring-destructive/25',
};

function StatusBadge({ status, className = '' }) {
  const style = STATUS_STYLES[status] || 'bg-white/5 text-muted-foreground ring-white/10';
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ring-1 ${style} ${className}`}>
      {status}
    </span>
  );
}

function EmptyState({ icon, children }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 rounded-xl py-16 text-center ring-1 ring-foreground/[0.06]">
      <Icon icon={icon} className="size-6 text-muted-foreground/40" />
      <p className="text-sm text-muted-foreground">{children}</p>
    </div>
  );
}

function MovieCard({ movie }) {
  const [showEpisodes, setShowEpisodes] = useState(false);
  const hasEpisodes = movie.Episodes?.length > 0;

  return (
    <div className="mb-2.5 rounded-lg bg-white/[0.03] p-3.5 ring-1 ring-foreground/[0.06]">
      <div className="flex flex-wrap items-center gap-2.5">
        <Icon icon="ph:film-slate-bold" className="shrink-0 text-muted-foreground/60" />
        <strong className="text-sm text-foreground">{movie.Title}</strong>
        <StatusBadge status={movie.ApprovalStatus} />
        <span className="text-xs text-muted-foreground">{movie.ContentType}</span>
        {movie.CategoryName && <span className="text-xs text-muted-foreground">{movie.CategoryName}</span>}
        {movie.ContentType === 'Series' && movie.SeasonCount > 0 && (
          <span className="text-xs text-muted-foreground">{movie.SeasonCount} season{movie.SeasonCount > 1 ? 's' : ''}</span>
        )}
        <span className="text-xs text-muted-foreground">
          {movie.ApprovedEpisodeCount}/{movie.EpisodeCount} episode{movie.EpisodeCount === 1 ? '' : 's'} live
          {movie.PendingEpisodeCount > 0 && <> · {movie.PendingEpisodeCount} pending</>}
        </span>
        <span className="text-xs text-muted-foreground/60">{movie.DateCreated?.split('T')[0]}</span>

        {hasEpisodes && (
          <button
            onClick={() => setShowEpisodes((s) => !s)}
            className="ml-auto flex items-center gap-1 text-xs font-medium text-primary transition-colors hover:text-primary/80"
          >
            <Icon icon={showEpisodes ? 'ph:caret-up-bold' : 'ph:caret-down-bold'} />
            {showEpisodes ? 'Hide' : 'Show'} episodes
          </button>
        )}
      </div>

      {showEpisodes && hasEpisodes && (
        <div className="mt-2.5 flex flex-col gap-1.5 border-t border-white/5 pt-2.5">
          {Object.entries(
            movie.Episodes.reduce((groups, ep) => {
              const key = ep.SeasonNumber ?? '__none__';
              (groups[key] = groups[key] || []).push(ep);
              return groups;
            }, {})
          ).map(([seasonKey, eps]) => (
            <div key={seasonKey}>
              {seasonKey !== '__none__' && (
                <p className="my-1 text-xs font-semibold text-muted-foreground">Season {seasonKey}</p>
              )}
              {eps.map((ep) => (
                <div key={ep.Id} className="flex items-center gap-2.5 py-0.5 text-sm text-muted-foreground">
                  <Icon icon="ph:play-circle-bold" className="shrink-0 text-muted-foreground/40" />
                  <span>Ep {ep.EpisodeNumber}: {ep.Title}</span>
                  <StatusBadge status={ep.ApprovalStatus} />
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CreatorRow({ creator }) {
  const [expanded, setExpanded] = useState(false);
  const { data, isLoading, error } = useCreatorContent(creator.Id, expanded);
  const grantMutation = useGrantUploadAccess();
  const revokeMutation = useRevokeUploadAccess();

  const handleGrant = async (e) => {
    e.stopPropagation();
    try {
      await grantMutation.mutateAsync(creator.Id);
      toast.success(`Upload access granted to ${creator.FullName}.`);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to grant upload access.'));
    }
  };

  const handleRevoke = async (e) => {
    e.stopPropagation();
    try {
      await revokeMutation.mutateAsync(creator.Id);
      toast.success(`Upload access revoked for ${creator.FullName}.`);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to revoke upload access.'));
    }
  };

  return (
    <div className="mb-3 overflow-hidden rounded-xl ring-1 ring-foreground/[0.06]">
      <button
        onClick={() => setExpanded((e) => !e)}
        className="flex w-full items-center gap-3 bg-white/[0.03] px-5 py-4 text-left transition-colors hover:bg-white/[0.05]"
      >
        <Icon icon="ph:user-check-bold" className="shrink-0 text-pink-400" />
        <div className="min-w-0 flex-1">
          <div className="truncate font-semibold text-foreground">{creator.FullName}</div>
          <div className="truncate text-xs text-muted-foreground">@{creator.Username} · {creator.Email}</div>
        </div>
        <span
          className={`hidden shrink-0 items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold ring-1 sm:inline-flex ${
            creator.CanUploadContent
              ? 'bg-emerald-500/10 text-emerald-400 ring-emerald-500/25'
              : 'bg-primary/10 text-primary ring-primary/25'
          }`}
        >
          {creator.CanUploadContent ? 'Upload Access Granted' : 'Awaiting Approval'}
        </span>
        <span className="shrink-0 text-xs text-muted-foreground">{creator.MovieCount} title{creator.MovieCount === 1 ? '' : 's'}</span>
        <Icon icon={expanded ? 'ph:caret-up-bold' : 'ph:caret-down-bold'} className="shrink-0 text-muted-foreground" />
      </button>

      <div className="flex gap-2.5 border-t border-white/5 bg-white/[0.015] px-5 py-3">
        {creator.CanUploadContent ? (
          <Button
            size="sm"
            onClick={handleRevoke}
            disabled={revokeMutation.isPending}
            className="gap-1.5 bg-destructive/10 text-destructive ring-1 ring-destructive/25 hover:bg-destructive/20"
          >
            <Icon icon="ph:prohibit-bold" />
            Revoke Upload Access
          </Button>
        ) : (
          <Button size="sm" onClick={handleGrant} disabled={grantMutation.isPending} className="gap-1.5">
            <Icon icon="ph:upload-simple-bold" />
            Grant Upload Access
          </Button>
        )}
      </div>

      {expanded && (
        <div className="border-t border-white/5 bg-black/10 px-5 py-4">
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : error ? (
            <p className="text-sm text-destructive">{getApiErrorMessage(error, 'Failed to load content.')}</p>
          ) : data?.Movies?.length ? (
            data.Movies.map((m) => <MovieCard key={m.Id} movie={m} />)
          ) : (
            <p className="text-sm text-muted-foreground">No uploads yet.</p>
          )}
        </div>
      )}
    </div>
  );
}

export default function AdminContentCreatorsPage() {
  usePageTitle('Content Creators · Kast');
  const { data: creators = [], isLoading, error } = useContentCreators();

  return (
    <div className="px-4 py-6 sm:px-6 sm:py-8 lg:px-10">
      <div className="mb-6 flex items-center gap-3">
        <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-white/5 text-primary ring-1 ring-white/10">
          <Icon icon="ph:star-bold" className="size-5" />
        </span>
        <div>
          <h1 className="text-lg font-semibold text-foreground">Content Creators</h1>
          <p className="text-xs text-muted-foreground">
            Every verified content creator and everything they've uploaded — expand a creator to see their titles, and a title to see its episodes.
          </p>
        </div>
      </div>

      {isLoading ? (
        <EmptyState icon="ph:spinner-gap-bold">Loading…</EmptyState>
      ) : error ? (
        <EmptyState icon="ph:warning-bold">{getApiErrorMessage(error, 'Failed to load content creators.')}</EmptyState>
      ) : creators.length === 0 ? (
        <EmptyState icon="ph:star-bold">No verified content creators yet.</EmptyState>
      ) : (
        <div>
          {creators.map((c) => <CreatorRow key={c.Id} creator={c} />)}
        </div>
      )}
    </div>
  );
}
